/**
 * GDriveMediaBadge
 *
 * Small pill overlay shown on PostMedia thumbnails when the media was sourced
 * from Google Drive (sourceType === 'GDRIVE'). Communicates the deferred-
 * materialization model to the user: the file lives in Drive and will be
 * fetched at publish time.
 *
 * Only renders when media.sourceType === 'GDRIVE'. Safe to mount on every
 * media item — non-GDrive items simply render nothing.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGoogleDrive } from '@fortawesome/free-brands-svg-icons';
import { Tooltip } from '@/components/ui/Tooltip';

interface GDriveMediaBadgeProps {
  /** Media object. May or may not have a sourceType field. */
  media: {
    sourceType?: string;
  };
  /** Extra class names for positioning (e.g. 'absolute bottom-1 left-1'). */
  className?: string;
}

export const GDriveMediaBadge = ({ media, className = '' }: GDriveMediaBadgeProps) => {
  if (media.sourceType !== 'GDRIVE') {
    return null;
  }

  return (
    <Tooltip content="Fetched from Google Drive at publish time">
      <span
        className={[
          'inline-flex items-center gap-1',
          'px-1.5 py-0.5 rounded',
          'bg-white/90 shadow-sm',
          'text-[10px] font-medium text-gray-700',
          'leading-none select-none',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label="Google Drive media - fetched at publish time"
      >
        <FontAwesomeIcon icon={faGoogleDrive} style={{ fontSize: '9px' }} aria-hidden="true" />
        Drive
      </span>
    </Tooltip>
  );
};
